import {
  aliasOf,
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";

import NavBarViewModel, {
  navbarRoute,
  sortFields,
  sortPair,
} from "./NavBar/NavBarViewModel";
import NavBarT9N from "../../t9n/ui/NavBar/NavBar.json";
import HeaderT9N from "../../t9n/ui/Header/Header.json";
import { VNode } from "src/interfaces/interfaces";

import "./NavBar/styles/NavBar.scss";
import SearchBox from "../widgets/SearchBox";
import { init, whenDefinedOnce } from "@arcgis/core/core/watchUtils";
import { whenOnce } from "@arcgis/core/core/reactiveUtils";

import { applyShadowStyle, getBaseUrl } from "../utils/utils";
import { SHADOW_STYLE_NAVBAR } from "../utils/shadowStyles";
import {addUrlParam, removeUrlParam} from "../utils/urlUtils";

import t9nAppMessages from "../../t9n/App/App.json";

export interface NavBarProperties extends __esri.WidgetProperties {
  name?: string;
  view?: __esri.MapView | __esri.SceneView;
}

const MOBILE_VIEW_BREAK_POINT: number = 945; // (Needs to match hardcoded CSS val in Navbar.scss)

@subclass("Landing.ui.NavBar")
export default class NavBar extends Widget {
  @property()
  @messageBundle("dist/assets/t9n/ui/NavBar/NavBar")
  messages: typeof NavBarT9N;

  @property()
  @messageBundle("dist/assets/t9n/ui/Header/Header")
  headerMessages: typeof HeaderT9N;

  @property()
  @aliasOf("viewModel.appMessages")
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  TabStates;

  // @property()
  // selectedTab: string;

  @property()
  searchBox: SearchBox = new SearchBox();

  @property()
  @aliasOf("viewModel.portal")
  portal: __esri.Portal;

  @property({
    type: NavBarViewModel
  })
  viewModel: NavBarViewModel = new NavBarViewModel();

  @property()
  _sortTermsToSortFields: [string, sortFields][];
  @property()
  _isMobileView: boolean = false;

  @property()
  _resourceSwapModal: HTMLCalciteModalElement;
  @property()
  _newResourceItem: __esri.PortalItem;
  @property()
  _newResourceType: "webmap" | "webscene";
  @property()
  _orgUrl: string;

  constructor(properties?: NavBarProperties) {
    super(properties);
    window.addEventListener("resize", this._handleWindowResize.bind(this));
  }

  postInitialize() {
    this._handleWindowResize();
    whenDefinedOnce(this, "searchBox", () => {
      this.viewModel.searchBox = this.searchBox;
    });

    this.searchBox.navbarVM = this.viewModel;
    whenOnce(() => this.viewModel?.itemLoaderVM).then(() => {
      this._newResourceItem = this.viewModel.itemLoaderVM.getLoadedItem();
    });

    if (!this.portal.isPortal) {
      // Online Tabs
      this.TabStates = {
        browseAll: this.messages.BrowseAll,
        myApps: this.messages.MyApps,
        featured: this.messages.Featured,
      };
    } else {
      // Enterprise Tabs
      this.TabStates = {
        browseAll: this.messages.BrowseAll,
        myApps: this.messages.MyApps,
      };
    }

    // Default Tab
    this._performChangeTab(this.viewModel.navigationRoute ?? "browseAll");

    init(this.viewModel, "navigationRoute", (route: navbarRoute) => {
      // Update Sort for new Tab
      const sortFieldsForNewTab = this._decideSortFieldsForTab();
      this.viewModel.setCurrentSort(sortFieldsForNewTab);
      this._sortTermsToSortFields = sortFieldsForNewTab;

      // update Search for new Tab
      this.viewModel.setCurrentSearchFilter();
      this.scheduleRender();
    });

    this._orgUrl = getBaseUrl(this.portal).replace("http://", "https://");
    this.scheduleRender();
  }

  render() {
    const search = this._renderSearch();
    const nav = this._renderNav();
    const btns = this._renderButtons();

    return [
      <div class="navbar">
        {search}
        {nav}
        {btns}
      </div>,
      this.viewModel?.onboardingModal?.render(),
      this._shouldRenderFilterChips(),
    ];
  }

  private _decideSortFieldsForTab(): sortPair[] {
    const { sortingTerms } = this.appMessages;
    switch (this.viewModel.navigationRoute) {
      case "browseAll":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "featured":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "myApps":
        return [
          [sortingTerms.date, "modified"],
          [sortingTerms.name, "title"],
        ];
    }
  }

  private _renderSearch(): VNode {
    return <div class="search">{this?.searchBox?.render()}</div>;
  }

  private _renderNav(): VNode {
    return (
      <calcite-tabs
        layout="inline"
        position="above"
        class="links"
        afterCreate={(tabs) => applyShadowStyle(SHADOW_STYLE_NAVBAR, tabs)}
      >
        <calcite-tab-nav slot="tab-nav" class="links__tabs">
          {Object.keys(this?.TabStates)?.map((enumKey, index) => {
            return (
              <calcite-tab-title
                class="nav-option"
                key={`navOption${index}`}
                active={
                  this.TabStates[this.viewModel.navigationRoute] ===
                  this.TabStates[enumKey]
                    ? "true"
                    : "false"
                }
                onclick={this._performChangeTab.bind(this, enumKey)}
                onkeydown={this._handleKeyboardChangeTab.bind(this, enumKey)}
              >
                {this.TabStates[enumKey]}
              </calcite-tab-title>
            );
          })}
        </calcite-tab-nav>
      </calcite-tabs>
    );
  }

  private _renderButtons(): VNode {
    return (
      <div class="buttons">
        {this._renderResourceButton()}
        {this._renderResourceSwapModal()}

        {!this.portal.isPortal ? this._renderWelcomeButton() : null}
        {this._renderSort()}
      </div>
    );
  }

  private _renderWelcomeButton(): VNode {
    const { onboardingStrings } = this.appMessages;
    return (
      <calcite-button
        class="welcome-btn"
        onclick={() => this.viewModel.openOnboarding()}
        color="neutral"
        scale="m"
        title={onboardingStrings?.launchBtnText}
        appearance="transparent"
        icon-start="mega-phone"
      >
        {this.onlyShowTextWhenNotMobile(onboardingStrings?.launchBtnText)}
      </calcite-button>
    );
  }

  private _shouldRenderFilterChips(): VNode {
    return (this.viewModel.navigationRoute === "create" ||
      this.viewModel.navigationRoute === "browseAll") &&
      this?.viewModel?.filterChips?.chips?.length > 0
      ? this.viewModel?.filterChips?.render()
      : null;
  }

  private _renderSort(): VNode {
    const { sortingTerms } = this.appMessages;
    const { sortBy } = sortingTerms;
    return (
      <calcite-dropdown
        class="sort-dropdown"
        scale="l"
        width="m"
        disabled={this.viewModel.isFeaturedTab()}
        type="click"
        placement="bottom-end"
        afterCreate={this._handleDropdownSelection.bind(this)}
      >
        <calcite-button
          color="neutral"
          appearance="transparent"
          icon-start="sort-descending"
          slot="dropdown-trigger"
          alignment="center"
          scale="m"
          width="auto"
          title={sortBy}
        >
          {this.onlyShowTextWhenNotMobile(this.viewModel.selectedSortField)}
        </calcite-button>

        <calcite-dropdown-group
          selection-mode="single"
          group-title={sortBy}
          role="menu"
          scale="l"
          title={sortBy}
        >
          {this._renderDropdownItems()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  private onlyShowTextWhenNotMobile(text: string): string | null {
    return !this._isMobileView ? text : null;
  }

  private _handleDropdownSelection(
    dropdown: any
  ): void {
    dropdown.addEventListener("calciteDropdownSelect", (e: any) => {
      this.viewModel.handleSortSelectionChange(e);
    });
  }

  private _renderDropdownItems(): VNode[] {
    return this._sortTermsToSortFields?.map(
      (sortInfo: [string, sortFields], index: number) => {
        return (
          <calcite-dropdown-item
            key={`sortItem${index}`}
            role="menuitemradio"
            scale="l"
            selection-mode="single"
            tabindex="0"
            value={sortInfo[1]}
            active={sortInfo[1] === this.viewModel.sortField}
          >
            {sortInfo[0]}
          </calcite-dropdown-item>
        );
      }
    );
  }

  private _handleKeyboardChangeTab(
    enumKey: navbarRoute,
    keyEvent: KeyboardEvent
  ) {
    if (keyEvent.code === "Enter" || keyEvent.code === "Space") {
      this._performChangeTab(enumKey);
    }
  }

  private _performChangeTab(enumKey: navbarRoute) {
    if (enumKey != null) {
      this.viewModel.setNavbarRoute(enumKey);
      this.scheduleRender();
    }
  }

  private _handleWindowResize() {
    if (window.innerWidth <= MOBILE_VIEW_BREAK_POINT) {
      this._isMobileView = true;
      this.scheduleRender();
    } else {
      this._isMobileView = false;
      this.scheduleRender();
    }
  }

  private _renderResourceButton() {
    const { buttonText, clearMap } = this.appMessages.resourceSwap;
    return (
      this.viewModel.itemLoaderVM.loadedItemType === "none" ?
      <calcite-button
        color="neutral"
        appearance="transparent"
        icon-start="analysis"
        title={buttonText}
        disabled={this.viewModel.isFeaturedTab()}
        onclick={() => {
          this._resourceSwapModal.setAttribute("active", "true");
        }}
      >
        {this.onlyShowTextWhenNotMobile(buttonText)}
      </calcite-button>
      :
      <calcite-split-button
        color="neutral"
        appearance="transparent"
        primary-icon-start="analysis"
        title={buttonText}
        disabled={this.viewModel.isFeaturedTab()}
        primary-text={this.onlyShowTextWhenNotMobile(buttonText)}

        afterCreate={(splitButton: HTMLCalciteSplitButtonElement) => {
          const tryXButtonUpdate = (numOfTriesLeft: number)=>{
            const xButton: HTMLCalciteButtonElement = splitButton.shadowRoot.querySelector("calcite-button[slot='dropdown-trigger']");
            if(xButton != null){
              xButton.iconStart = "x";
              xButton.title = clearMap;
            } else if(numOfTriesLeft > 0) {
              setTimeout(tryXButtonUpdate.bind(this, numOfTriesLeft - 1), 500);
            } else {
              return;
            }
          };
          setTimeout(tryXButtonUpdate.bind(this, 5), 500);
        }}

        onCalciteSplitButtonPrimaryClick={() => {
          this._resourceSwapModal.setAttribute("active", "true");
        }}

        onCalciteSplitButtonSecondaryClick={(e)=>{
          window.location.assign(
            removeUrlParam(["webmap", "webscene", "group"])
          );
        }}
      >
      </calcite-split-button>
    );
  }

  @property() itemBrowserUpdate: any;

  private _renderResourceSwapModal() {
    const { updated, by } = this.appMessages.resourceSwap;
    return (
      <calcite-modal
        width="l"
        fullscreen={true}
        afterCreate={(modal: HTMLCalciteModalElement) => {
          this._resourceSwapModal = modal;
          modal.beforeClose = () => {
            return new Promise((resolve) => {
              this._resourceSwapModal.removeAttribute("active");
              resolve();
            });
          };
        }}
        intl-close={this.appMessages.modals.closeButtonTip}
      >
        <h2 slot="header">{this.appMessages.resourceSwap.modalTitle}</h2>

        <div slot="content" id="resource-switch-div">
          <arcgis-item-browser
            id="resource-switch-item-browser"
            user={this.portal.user}
            api={4}
            portal={this.portal}
            config={{baseUrl: `${this._orgUrl}/home/`} }
            preflight={(query: any) => {
              query.baseQuery = `(type:("Web Map" OR "Web Scene") -type:"Web Mapping Application")`;
              return query;
            }}
            selection="single"
            onArcgisItemBrowserSelect={this._handleSelection.bind(this)}
            onArcgisItemBrowserUpdate={(e) => {
              this.itemBrowserUpdate = e.detail;

              if(this._newResourceItem != null){
                // filter Currently selected item, if it is in list to display
                this.itemBrowserUpdate.results = this.itemBrowserUpdate.results.filter((item)=>{ return item?.id !== this?._newResourceItem?.id; })

                // ALWAYS PREPEND THE SELECTED ITEM TO THE BEGINNING OF THE LIST - such that it appears on the top for every pagination page
                this.itemBrowserUpdate.results.unshift(this._newResourceItem);
              }

              this.scheduleRender();
            }}
          >
            <arcgis-item-browser-top-bar slot="top-bar" filter-toggle>
              <arcgis-item-browser-bucket-select slot="bucket"></arcgis-item-browser-bucket-select>
              <arcgis-item-browser-search
                slot="search"
                term=""
              ></arcgis-item-browser-search>
              <arcgis-item-browser-sort-dropdown
                slot="sort"
                field="modified"
              ></arcgis-item-browser-sort-dropdown>
            </arcgis-item-browser-top-bar>
            <arcgis-item-browser-filters slot="filters">
              <arcgis-item-browser-filter-folder expanded="true"></arcgis-item-browser-filter-folder>
              <arcgis-item-browser-filter-date></arcgis-item-browser-filter-date>
              <arcgis-item-browser-filter-tags></arcgis-item-browser-filter-tags>
            </arcgis-item-browser-filters>
            <arcgis-item-browser-content slot="content" class="js-content">
              {this.itemBrowserUpdate?.results
              .map((item, index) => {
                return (
                  <arcgis-item-browser-card
                    key={`item-browser-card-${item.id}`}
                    item={item}
                    portal={this.portal}
                    user={this.portal.user}
                    selected={this._newResourceItem?.id === item?.id}
                    selection="single"
                    preview="action"
                    name="item-browser-demo"
                  >
                    <div slot="content-end" class="card-extra-content">
                      <calcite-link
                        onclick={(e: Event) => {
                          e.stopPropagation();
                        }}
                        icon-end="launch"
                        href={`${this._orgUrl}/home/item.html?id=${item?.id}`}
                        target="_blank"
                      >
                        {this.appMessages.hamburgerBtns.viewItemDetails}
                      </calcite-link>
                    </div>
                  </arcgis-item-browser-card>
                );
              })
            }
            </arcgis-item-browser-content>
            <arcgis-item-browser-pagination
              slot="pagination"
              start="1"
              num="20"
              class="js-pagination"
              total={this?.itemBrowserUpdate?.total}
            ></arcgis-item-browser-pagination>
          </arcgis-item-browser>
        </div>

        <calcite-button
          key="next-close-button"
          bind={this}
          id="onboardingButton"
          scale="l"
          onclick={() => {
            window.location.assign(
            addUrlParam(
              this._newResourceType,
              this._newResourceItem.id,
              removeUrlParam(["webmap", "webscene", "group"]))
            );
          }}
          slot="primary"
          disabled={this._newResourceItem?.id == null}
          width="full"
        >
          {this.appMessages.resourceSwap.confirmText}
        </calcite-button>
        <calcite-button
          key="next-close-button"
          bind={this}
          id="onboardingButton"
          scale="l"
          onclick={() => {
            this._resourceSwapModal.removeAttribute("active");
          }}
          slot="secondary"
          width="full"
          appearance="outline"
        >
          {this.appMessages.resourceSwap.cancelText}
        </calcite-button>
      </calcite-modal>
    );
  }

  private _handleSelection(e) {
    const resource = e?.detail?.[0];
    this._newResourceItem = resource;
    this._newResourceType =
      resource?.type === "Web Map"
        ? "webmap"
        : resource?.type === "Web Scene"
        ? "webscene"
        : null;
  }
}
